import Gooey, {
    Component,
    calc,
    Dyn,
    dynGet,
    dynSet,
    dynSubscribe,
    field,
    model,
    ref,
} from '@srhazi/gooey';
import './marquee-button.css';

const safeMod = (a: number, b: number) => ((a % b) + b) % b;

export const MarqueeButton: Component<{
    label: Dyn<string>;
    width?: Dyn<string | number>;
    height?: Dyn<string | number>;
    gapX?: Dyn<number>;
    gapY?: Dyn<number>;
    scrollEven?: Dyn<number>;
    scrollOdd?: Dyn<number>;
    angle?: Dyn<number>;
    forceActive?: Dyn<boolean>;
}> = (
    {
        label,
        width = 'auto',
        height = 'auto',
        gapX = 10,
        gapY = 10,
        scrollEven = 47,
        scrollOdd = -50,
        angle = Math.PI / 6,
        forceActive = false,
    },
    { onMount }
) => {
    const hasFocus = field(false);
    const hasHover = field(false);
    const isActive = calc(
        () => hasFocus.get() || hasHover.get() || dynGet(forceActive)
    );
    const evenShift = field(0.5);
    const oddShift = field(0);

    const elRef = ref<HTMLDivElement>();
    const offscreenRef = ref<HTMLDivElement>();
    const scrollerRef = ref<HTMLDivElement>();

    const rawDimensions = model({
        width: 0,
        height: 0,
    });

    const labelDimensions = calc(() => {
        if (!isActive.get() || !offscreenRef.current) {
            return null;
        }
        const spanEl = document.createElement('span');
        spanEl.style.whiteSpace = 'nowrap';
        spanEl.style.display = 'inline-block';
        spanEl.textContent = dynGet(label);
        offscreenRef.current.replaceChildren(spanEl);
        const rect = spanEl.getBoundingClientRect();
        return { width: rect.width, height: rect.height };
    });

    const nodeInfo = calc(() => {
        if (!isActive.get()) {
            return null;
        }
        const labelDims = labelDimensions.get();
        if (!labelDims) {
            return null;
        }
        const bounds = {
            width: Math.ceil(labelDims.width + dynGet(gapX)),
            height: Math.ceil(labelDims.height + dynGet(gapY)),
        };
        const rotatedWidth =
            rawDimensions.height * Math.abs(Math.sin(dynGet(angle))) +
            rawDimensions.width * Math.abs(Math.cos(dynGet(angle)));
        const rotatedHeight =
            rawDimensions.height * Math.abs(Math.cos(dynGet(angle))) +
            rawDimensions.width * Math.abs(Math.sin(dynGet(angle)));
        let numNodesH = Math.ceil(rotatedWidth / bounds.width) + 2;
        let numNodesV = Math.ceil(rotatedHeight / bounds.height) + 1;
        // Need an even number of nodes in order to keep things centered correctly
        if (numNodesH & 1) {
            numNodesH++;
        }
        if (numNodesV & 1) {
            numNodesV++;
        }
        const totalWidth = numNodesH * bounds.width - dynGet(gapX);
        const totalHeight = numNodesV * bounds.height - dynGet(gapY);
        const totalShiftX = -(totalWidth - rawDimensions.width) / 2;
        const totalShiftY = -(totalHeight - rawDimensions.height) / 2;
        const evenNode = document.createElement('span');
        evenNode.textContent = dynGet(label);
        const oddNode = evenNode.cloneNode(true) as HTMLSpanElement;
        evenNode.className = 'marquee-button_item--even';
        oddNode.className = 'marquee-button_item--odd';
        const labelNodes: HTMLSpanElement[] = [];
        for (let x = 0; x < numNodesH; ++x) {
            for (let y = 0; y < numNodesV; ++y) {
                const toClone = y & 1 ? oddNode : evenNode;
                const cloned = toClone.cloneNode(true) as HTMLSpanElement;
                cloned.style.setProperty(
                    '--marquee-button_item-col',
                    x.toString()
                );
                cloned.style.setProperty(
                    '--marquee-button_item-row',
                    y.toString()
                );
                labelNodes.push(cloned);
            }
        }
        return { bounds, labelNodes, totalShiftX, totalShiftY };
    });

    const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            if (entry.target === elRef.current) {
                if (entry.borderBoxSize) {
                    const box = entry.borderBoxSize[0];
                    rawDimensions.width = box.inlineSize;
                    rawDimensions.height = box.blockSize;
                } else {
                    rawDimensions.width = entry.contentRect.width;
                    rawDimensions.height = entry.contentRect.height;
                }
            }
        }
    });

    let animationHandle: null | number = null;
    let lastTickMs: null | number = null;
    const tick = (ms: number) => {
        if (lastTickMs === null) {
            lastTickMs = ms;
        }
        let dt = (ms - lastTickMs) / 1000;
        lastTickMs = ms;
        const nodeWidth = nodeInfo.get()?.bounds.width;
        let es = evenShift.get() + dynGet(scrollEven) * dt;
        let os = oddShift.get() + dynGet(scrollOdd) * dt;
        if (nodeWidth) {
            es = safeMod(es, nodeWidth);
            os = safeMod(os, nodeWidth);
        }
        evenShift.set(es);
        oddShift.set(os);

        animationHandle = requestAnimationFrame(tick);
    };

    onMount(() => {
        if (elRef.current) {
            resizeObserver.observe(elRef.current);
        }
        const unsubscribe = isActive.subscribe((val) => {
            if (val) {
                evenShift.set(0);
                oddShift.set(50);
                animationHandle = requestAnimationFrame(tick);
            } else if (animationHandle) {
                cancelAnimationFrame(animationHandle);
                animationHandle = null;
            }
        });
        return () => {
            resizeObserver.disconnect();
            if (animationHandle) {
                cancelAnimationFrame(animationHandle);
                animationHandle = null;
            }
            unsubscribe();
        };
    });

    // TODO: bug in gooey where cssprop: props are not bound to dynamic values
    return (
        <div
            ref={elRef}
            class={calc(
                () =>
                    `marquee-button ${
                        isActive.get() ? 'marquee-button--active' : ''
                    }`
            )}
            on:focusin={() => hasFocus.set(true)}
            on:focusout={() => hasFocus.set(false)}
            on:mouseenter={() => hasHover.set(true)}
            on:mouseleave={() => hasHover.set(false)}
            style={calc(() => {
                const widthRaw = dynGet(width);
                const widthStr =
                    typeof widthRaw === 'string' ? widthRaw : `${widthRaw}px`;
                const heightRaw = dynGet(height);
                const heightStr =
                    typeof heightRaw === 'string'
                        ? heightRaw
                        : `${heightRaw}px`;
                return [
                    `--marquee-button_width: ${widthStr}`,
                    `--marquee-button_height: ${heightStr}`,
                    `--marquee-button_calc_width: ${rawDimensions.width}px`,
                    `--marquee-button_calc_height: ${rawDimensions.height}px`,
                ].join(';');
            })}
        >
            <div class="marquee-button_overflow">
                <div class="marquee-button_scroller" ref={scrollerRef}>
                    <div
                        role="presentation"
                        aria-hidden="true"
                        class="marquee-button_offscreen"
                        ref={offscreenRef}
                    ></div>
                    <div
                        role="presentation"
                        aria-hidden="true"
                        class="marquee-button_shifter"
                        style={calc(() => {
                            const info = nodeInfo.get();
                            if (!info) {
                                return '';
                            }
                            return [
                                `--marquee-button_shifter_angle: ${dynGet(
                                    angle
                                )}`,
                                `--marquee-button_total-shift-x: ${info.totalShiftX}px`,
                                `--marquee-button_total-shift-y: ${info.totalShiftY}px`,
                                `--marquee-button_even-shift: ${evenShift.get()}px`,
                                `--marquee-button_odd-shift: ${oddShift.get()}px`,
                                `--marquee-button_item-width: ${info.bounds.width}px`,
                                `--marquee-button_item-height: ${info.bounds.height}px`,
                            ].join(';');
                        })}
                    >
                        {calc(() => nodeInfo.get()?.labelNodes)}
                    </div>
                </div>
                <button class="marquee-button_button">{label}</button>
            </div>
        </div>
    );
};
